import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceMetrics = ({ metrics = {} }) => {
  const metricCards = [
    {
      key: 'totalCalls',
      label: 'Total Calls',
      value: metrics.totalCalls || 0,
      icon: 'Phone',
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
      textColor: 'text-blue-700 dark:text-blue-300',
      change: metrics.totalCalls > 50 ? '+12%' : metrics.totalCalls > 30 ? '+8%' : '+5%'
    },
    {
      key: 'successfulCalls',
      label: 'Successful Calls',
      value: metrics.successfulCalls || 0,
      icon: 'PhoneCall',
      color: 'bg-green-500',
      bgColor: 'bg-green-50 dark:bg-green-950',
      textColor: 'text-green-700 dark:text-green-300',
      change: metrics.successRate > 60 ? '+15%' : metrics.successRate > 40 ? '+7%' : '+3%'
    },
    {
      key: 'validLeads',
      label: 'Valid Leads',
      value: metrics.validLeads || 0,
      icon: 'CheckCircle',
      color: 'bg-emerald-500',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950',
      textColor: 'text-emerald-700 dark:text-emerald-300',
      change: metrics.conversionRate > 30 ? '+18%' : metrics.conversionRate > 20 ? '+10%' : '+4%'
    },
    {
      key: 'successRate',
      label: 'Success Rate',
      value: `${metrics.successRate || 0}%`,
      icon: 'TrendingUp',
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-950',
      textColor: 'text-purple-700 dark:text-purple-300',
      change: metrics.successRate > 65 ? '+5%' : metrics.successRate > 50 ? '+2%' : '-1%'
    },
    {
      key: 'conversionRate',
      label: 'Conversion Rate',
      value: `${metrics.conversionRate || 0}%`,
      icon: 'Target',
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50 dark:bg-orange-950',
      textColor: 'text-orange-700 dark:text-orange-300',
      change: metrics.conversionRate > 35 ? '+8%' : metrics.conversionRate > 25 ? '+4%' : '+1%'
    },
    {
      key: 'avgCallDuration',
      label: 'Avg Call Time',
      value: `${Math.floor((metrics.avgCallDuration || 0) / 60)}:${((metrics.avgCallDuration || 0) % 60).toString().padStart(2, '0')}`,
      icon: 'Clock',
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50 dark:bg-indigo-950',
      textColor: 'text-indigo-700 dark:text-indigo-300',
      change: metrics.avgCallDuration > 180 ? '+6%' : metrics.avgCallDuration > 120 ? '+2%' : '-2%'
    }
  ];

  // Add new metrics for additional insights
  const additionalMetrics = [
    {
      key: 'newLeadsToday',
      label: 'New Leads Today',
      value: metrics.newLeadsToday || 0,
      icon: 'UserPlus',
      color: 'bg-cyan-500',
      bgColor: 'bg-cyan-50 dark:bg-cyan-950',
      textColor: 'text-cyan-700 dark:text-cyan-300',
      change: null
    },
    {
      key: 'scheduledCallbacks',
      label: 'Scheduled Callbacks',
      value: metrics.scheduledCallbacks || 0,
      icon: 'Calendar',
      color: 'bg-rose-500',
      bgColor: 'bg-rose-50 dark:bg-rose-950',
      textColor: 'text-rose-700 dark:text-rose-300',
      change: null
    },
    {
      key: 'hotLeads',
      label: 'Hot Leads',
      value: metrics.hotLeads || 0,
      icon: 'Flame',
      color: 'bg-red-500',
      bgColor: 'bg-red-50 dark:bg-red-950',
      textColor: 'text-red-700 dark:text-red-300',
      change: null
    }
  ];

  const getPerformanceLevel = () => {
    const conversionRate = parseFloat(metrics.conversionRate) || 0;
    const successRate = parseFloat(metrics.successRate) || 0;
    
    if (conversionRate >= 30 && successRate >= 70) return { level: 'Excellent', color: 'text-green-600' };
    if (conversionRate >= 20 && successRate >= 60) return { level: 'Good', color: 'text-blue-600' };
    if (conversionRate >= 10 && successRate >= 40) return { level: 'Average', color: 'text-orange-600' };
    return { level: 'Needs Improvement', color: 'text-red-600' };
  };

  const performance = getPerformanceLevel();
  const currentDateTime = new Date().toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Performance Metrics</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Last 7 days performance overview • Updated: {currentDateTime}
          </p>
        </div>
        
        <div className="flex items-center gap-4 mt-4 sm:mt-0">
          <div className="text-center">
            <p className={`text-lg font-bold ${performance.color}`}>
              {performance.level}
            </p>
            <p className="text-xs text-muted-foreground">Performance</p>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>7-day period</span>
          </div>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6">
        {metricCards.map((metric) => (
          <div key={metric.key} className={`${metric.bgColor} p-3 sm:p-4 rounded-lg border border-border`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`p-1.5 rounded-md ${metric.color}`}>
                <Icon name={metric.icon} size={14} color="white" />
              </div>
              {metric.change && (
                <span className={`text-xs ${
                  metric.change.startsWith('+') ? 'text-green-600' : 
                  metric.change.startsWith('-') ? 'text-red-600' : 'text-muted-foreground'
                }`}>
                  {metric.change}
                </span>
              )}
            </div>
            
            <div>
              <p className="text-lg sm:text-xl font-bold text-foreground">{metric.value}</p>
              <p className={`text-xs sm:text-sm font-medium ${metric.textColor} mt-1 leading-tight`}>
                {metric.label}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        {additionalMetrics.map((metric) => (
          <div key={metric.key} className={`${metric.bgColor} p-3 sm:p-4 rounded-lg border border-border`}>
            <div className="flex items-center justify-center mb-2">
              <div className={`p-1.5 rounded-md ${metric.color}`}>
                <Icon name={metric.icon} size={14} color="white" />
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-lg sm:text-xl font-bold text-foreground">{metric.value}</p>
              <p className={`text-xs sm:text-sm font-medium ${metric.textColor} mt-1 leading-tight`}>
                {metric.label}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Performance Insights */}
      <div className="p-4 bg-muted/30 rounded-lg">
        <h3 className="font-medium text-foreground mb-3">Performance Insights</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
          <div className="flex items-center gap-2">
            {(metrics.successRate || 0) >= 60 ? (
              <Icon name="CheckCircle" size={16} className="text-green-600" />
            ) : (
              <Icon name="AlertCircle" size={16} className="text-orange-600" />
            )}
            <span className="text-foreground">
              Success Rate: {(metrics.successRate || 0) >= 60 ? 'Above Target' : 'Below Target (60%)'}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {(metrics.conversionRate || 0) >= 20 ? (
              <Icon name="CheckCircle" size={16} className="text-green-600" />
            ) : (
              <Icon name="AlertCircle" size={16} className="text-orange-600" />
            )}
            <span className="text-foreground">
              Conversion: {(metrics.conversionRate || 0) >= 20 ? 'Above Target' : 'Below Target (20%)'}
            </span>
          </div>
        </div>

        {/* Daily Goals Progress */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Daily Goals Progress</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Calls Target (50)</span>
              <div className="flex items-center gap-2">
                <div className="w-24 bg-muted rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min((metrics.totalCalls || 0) / 50 * 100, 100)}%` }}
                  ></div>
                </div>
                <span className="text-xs text-foreground font-medium">
                  {Math.min((metrics.totalCalls || 0) / 50 * 100, 100).toFixed(0)}%
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Valid Leads (15)</span>
              <div className="flex items-center gap-2">
                <div className="w-24 bg-muted rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min((metrics.validLeads || 0) / 15 * 100, 100)}%` }}
                  ></div>
                </div>
                <span className="text-xs text-foreground font-medium">
                  {Math.min((metrics.validLeads || 0) / 15 * 100, 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Quick Tips */}
        <div className="pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground font-medium mb-2">Quick Tips for Today:</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            {(metrics.successRate || 0) < 60 && (
              <li>• Try calling during 10-11 AM or 2-4 PM for better response rates</li>
            )}
            {(metrics.conversionRate || 0) < 20 && (
              <li>• Ask better qualifying questions to identify serious candidates</li>
            )}
            {(metrics.avgCallDuration || 0) < 120 && (
              <li>• Spend 3-5 minutes understanding candidate requirements thoroughly</li>
            )}
            {(metrics.totalCalls || 0) < 40 && (
              <li>• Aim for 50+ daily calls to maximize lead generation opportunities</li>
            )}
            {(metrics.scheduledCallbacks || 0) > 0 && (
              <li>• You have {metrics.scheduledCallbacks} callbacks scheduled - check your calendar</li>
            )}
            {(metrics.hotLeads || 0) > 0 && (
              <li>• Focus on {metrics.hotLeads} hot leads - they are most likely to convert</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;